import { api } from '~/lib/api-client';

/**
 * How long one upload attempt may take before it counts as a stalled
 * connection: a minute, plus the file's size at 50 KB/s (a weak mobile
 * uplink). `fetch` has no time limit of its own, so a connection that stalls
 * instead of dropping left the phone's "Adding..." chip waiting forever, with
 * Send locked, until the app was restarted (FRONT-3427). A stall now fails
 * like a drop, and the upload retry resends it.
 */
export function uploadTimeoutMs(bytes: number): number {
  return 60_000 + Math.ceil(bytes / 50_000) * 1_000;
}

/**
 * One multipart upload attempt with a stall limit. Call it from inside a
 * `mutationFn`: a retry runs the function again and gets a fresh timer.
 *
 * `cancel` is the person's own stop (they removed the upload). A cancel
 * rejects with an `AbortError`, which the upload retry never resends. A stall
 * rejects with a `TimeoutError`, which it does resend.
 *
 * The rejection is decided by WHO aborted, not by the error `fetch` threw: a
 * browser may reject an aborted `fetch` with a plain `AbortError` and drop
 * the abort reason, and then a stall would read as a cancel and never be
 * resent.
 */
export async function sendUpload<T>(
  path: string,
  body: FormData,
  bytes: number,
  cancel?: AbortSignal,
): Promise<T> {
  const attempt = new AbortController();
  let stalled = false;
  const timer = setTimeout(() => {
    stalled = true;
    attempt.abort();
  }, uploadTimeoutMs(bytes));
  const stop = () => attempt.abort();
  if (cancel?.aborted) stop();
  cancel?.addEventListener('abort', stop);
  try {
    return await api.form<T>(path, body, attempt.signal);
  } catch (err) {
    if (cancel?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
    if (stalled) throw new DOMException('The upload stalled', 'TimeoutError');
    throw err;
  } finally {
    clearTimeout(timer);
    cancel?.removeEventListener('abort', stop);
  }
}
